import React, { useState } from 'react';
import { Eye, ArrowRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FadeIn, SlideInLeft, ScaleIn } from './ScrollReveal';
import { getProjects } from '../utils/cms';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showAll, setShowAll] = useState(false);

  // Load projects from CMS
  const projects = getProjects();

  const categories = [
    { id: 'all', name: 'Alle Projekte' },
    { id: 'bathroom', name: 'Badezimmer' },
    { id: 'special', name: 'Besondere Projekte' }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  // Show only first 6 projects initially, unless showAll is true
  const displayedProjects = showAll ? filteredProjects : filteredProjects.slice(0, 6);
  const hasMoreProjects = filteredProjects.length > 6;



  const closeLightbox = () => {
    setSelectedImage(null);
  };



  return (
    <section id="gallery" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <FadeIn>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-stone-800 mb-6 tracking-wide">
              Unsere Projekte
            </h2>
            <p className="text-lg text-stone-600 font-light leading-relaxed max-w-2xl mx-auto">
              Entdecken Sie die Vielfalt unserer realisierten Projekte. Jeder Raum erzählt seine eigene Geschichte von Qualität und Präzision.
            </p>
          </div>
        </FadeIn>

        {/* Filter Buttons */}
        <SlideInLeft delay={200}>
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => {
                  setActiveFilter(category.id);
                  setShowAll(false); // Reset to show only first 6 when filter changes
                }}
                className={`px-6 py-3 text-sm font-light tracking-wide transition-all duration-300 ${
                  activeFilter === category.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </SlideInLeft>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedProjects.map((project, index) => (
            <ScaleIn key={project.id} delay={index * 150} duration={600}>
              <Link to={`/projekte/${project.slug}`} className="group block">
                <div className="relative overflow-hidden bg-stone-100 aspect-[4/3]">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="text-center text-white">
                      <Eye className="w-8 h-8 mx-auto mb-2" />
                      <p className="text-sm font-light tracking-wide">Projekt ansehen</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6">
                  <h3 className="text-xl font-light text-stone-800 mb-2 group-hover:text-primary-600 transition-colors duration-300">{project.title}</h3>
                  <p className="text-stone-600 font-light leading-relaxed text-sm">
                    {project.description}
                  </p>
                  
                  {/* Project Meta Info */}
                  {(project.client || project.location) && (
                    <div className="mt-3 text-xs text-stone-500 space-y-1">
                      {project.client && <p>Kunde: {project.client}</p>}
                      {project.location && <p>Ort: {project.location}</p>}
                    </div>
                  )}
                </div>
              </Link>
            </ScaleIn>
          ))}
        </div>

        {/* Mehr anzeigen Button */}
        {hasMoreProjects && !showAll && (
          <FadeIn delay={400}>
            <div className="text-center mt-12">
              <button
                onClick={() => setShowAll(true)}
                className="inline-flex items-center justify-center bg-primary-600 text-white px-8 py-4 rounded-xl hover:bg-primary-700 transition-colors duration-300 font-medium text-lg"
              >
                Mehr anzeigen
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </FadeIn>
        )}

        {/* CTA Section */}
        <FadeIn delay={600}>
          <div className="text-center mt-16">
            <p className="text-stone-600 font-light mb-8 text-lg">
              Haben Sie Fragen zu unseren Projekten?
            </p>
            <a 
              href="#contact"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-light tracking-wide transition-colors duration-300"
            >
              Beratungstermin vereinbaren
              <ArrowRight className="ml-2 w-4 h-4" />
            </a>
          </div>
        </FadeIn>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl w-full">
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 text-white hover:text-stone-300 transition-colors duration-200"
            >
              <X className="w-8 h-8" />
            </button>
            
            <img
              src={selectedImage.image}
              alt={selectedImage.title}
              className="w-full h-auto max-h-[80vh] object-contain"
            />
            
            <div className="mt-6 text-center">
              <h3 className="text-2xl font-light text-white mb-2">{selectedImage.title}</h3>
              <p className="text-stone-300 font-light leading-relaxed">
                {selectedImage.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery; 